import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  OnInit,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  Achievement,
  AwardType,
  CompetitionsService,
  DistributionMode,
  UpdateAchievementInput,
} from '../../../core/competitions/competitions';

/**
 * Edit how an achievement pays out — award type, points or prize, and
 * whether the award is split — for any achievement except Age (computed per
 * death). Deliberately cannot change whether it comes from the catalog or is
 * custom: to change that, remove it and add a new one. A catalog
 * achievement's name and description come from the catalog, so they're shown
 * but not editable; a custom one's are.
 */
@Component({
  selector: 'app-achievement-edit-modal',
  imports: [ReactiveFormsModule],
  templateUrl: './achievement-edit-modal.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './achievement-edit-modal.css',
})
export class AchievementEditModal implements OnInit {
  private readonly competitions = inject(CompetitionsService);

  readonly achievement = input.required<Achievement>();
  readonly saved = output<Achievement>();
  readonly closed = output<void>();

  readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);

  readonly form = new FormGroup({
    name: new FormControl('', { nonNullable: true }),
    description: new FormControl('', { nonNullable: true }),
    awardType: new FormControl<AwardType>('POINTS', { nonNullable: true }),
    distributionMode: new FormControl<DistributionMode>('DUPLICATED', { nonNullable: true }),
    pointsValue: new FormControl<number | null>(null),
    prizeDescription: new FormControl('', { nonNullable: true }),
    prizeDollarValue: new FormControl<number | null>(null),
  });

  ngOnInit(): void {
    const a = this.achievement();
    this.form.setValue({
      name: a.name ?? '',
      description: a.description ?? '',
      awardType: a.awardType,
      distributionMode: a.distributionMode,
      pointsValue: a.pointsValue,
      prizeDescription: a.prizeDescription ?? '',
      prizeDollarValue: a.prizeDollarValue === null ? null : Number(a.prizeDollarValue),
    });
  }

  /** A catalog achievement takes its name/description from the catalog. */
  isCustom(): boolean {
    return this.achievement().achievementDefinitionId === null;
  }

  displayName(): string {
    const a = this.achievement();
    return a.achievementDefinition?.name ?? a.name ?? 'Achievement';
  }

  displayDescription(): string | null {
    const a = this.achievement();
    return a.achievementDefinition?.description ?? a.description;
  }

  close(): void {
    this.closed.emit();
  }

  async save(): Promise<void> {
    if (this.saving()) return;
    const raw = this.form.getRawValue();
    const input = this.buildInput(raw);
    if (typeof input === 'string') {
      this.errorMessage.set(input);
      return;
    }

    this.saving.set(true);
    this.errorMessage.set(null);
    try {
      const updated = await this.competitions.updateAchievement(
        this.achievement().competitionId,
        this.achievement().id,
        input,
      );
      this.saved.emit(updated);
    } catch (err) {
      const serverMessage =
        err instanceof HttpErrorResponse && typeof err.error?.message === 'string'
          ? err.error.message
          : null;
      this.errorMessage.set(serverMessage ?? 'Could not save — try again in a moment.');
    } finally {
      this.saving.set(false);
    }
  }

  /** The request body, or a validation message if the form isn't ready. */
  private buildInput(raw: ReturnType<typeof this.form.getRawValue>): UpdateAchievementInput | string {
    const input: UpdateAchievementInput = {
      awardType: raw.awardType,
      distributionMode: raw.distributionMode,
    };

    if (this.isCustom()) {
      if (!raw.name.trim()) return 'Name is required.';
      if (!raw.description.trim()) return 'Description is required.';
      input.name = raw.name.trim();
      input.description = raw.description.trim();
    }

    if (raw.awardType === 'POINTS') {
      if (raw.pointsValue === null || !Number.isInteger(raw.pointsValue) || raw.pointsValue < 0) {
        return 'Points must be a whole number, 0 or more.';
      }
      input.pointsValue = raw.pointsValue;
    } else {
      if (!raw.prizeDescription.trim()) return 'Prize description is required.';
      if (raw.prizeDollarValue !== null && !(raw.prizeDollarValue >= 0)) {
        return 'Dollar value must be 0 or more.';
      }
      input.prizeDescription = raw.prizeDescription.trim();
      input.prizeDollarValue = raw.prizeDollarValue;
    }
    return input;
  }
}
